import { useLazyQuery, useMutation } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';

import { GET_WAREHOUSE_PALLETS } from 'src/api/warehouses.api';
import ScanningApi from 'src/modules/scanning/api/Scanning.api';
import type {
  CreatePalletTags,
  CreatePalletTagsVariables,
  DeletePalletTags,
  DeletePalletTagsVariables,
  LookupPalletTag,
  PrintPalletTags,
  PrintPalletTagsVariables,
  UpdatePalletTag,
  UpdatePalletTagVariables,
} from 'types/graphql';

/** `createPalletTags` (P7-R2): toasts, then calls `onCompleted`. */
export const useCreatePalletTags = (onCompleted?: () => unknown) => {
  const [mutate, { loading, error }] = useMutation<CreatePalletTags, CreatePalletTagsVariables>(
    gql`
      mutation CreatePalletTags($input: CreatePalletTagsInput!) {
        createPalletTags(input: $input) {
          ...PalletTagAttributes
        }
      }
    `,
    {
      onCompleted: () => {
        enqueueSnackbar('Pallet Tag Created', { variant: 'success', autoHideDuration: 1000 });
        onCompleted?.();
      },
    }
  );
  const create = useCallback((variables: CreatePalletTagsVariables) => mutate({ variables }), [mutate]);

  return { create, loading, error };
};

/** `updatePalletTag` (P7-R2): `update({ id, input })`, refetching the warehouse pallet list. */
export const useUpdatePalletTag = (onCompleted?: () => unknown) => {
  const [mutate, { loading, error }] = useMutation<UpdatePalletTag, UpdatePalletTagVariables>(
    gql`
      mutation UpdatePalletTag($id: Int!, $input: UpdatePalletTagInput!) {
        updatePalletTag(id: $id, input: $input) {
          printed
          warehouseLocationId
        }
      }
    `,
    { refetchQueries: [GET_WAREHOUSE_PALLETS], onCompleted }
  );
  const update = useCallback((variables: UpdatePalletTagVariables) => mutate({ variables }), [mutate]);

  return { update, loading, error };
};

/** `deletePalletTags` (P7-R2): `remove({ ids })`, toasts, then calls `onCompleted`. */
export const useDeletePalletTags = (onCompleted?: () => unknown) => {
  const [mutate, { loading, error }] = useMutation<DeletePalletTags, DeletePalletTagsVariables>(
    gql`
      mutation DeletePalletTags($ids: [Int]!) {
        deletePalletTags(ids: $ids) {
          count
        }
      }
    `,
    {
      onCompleted: () => {
        enqueueSnackbar('Pallet Tags Deleted', { variant: 'success', autoHideDuration: 1000 });
        onCompleted?.();
      },
    }
  );
  const remove = useCallback((variables: DeletePalletTagsVariables) => mutate({ variables }), [mutate]);

  return { remove, loading, error };
};

/** `printPalletTags` (P7-R2): `print({ ids })`, toasts, and marks the selected tags printed. */
export const usePrintPalletTags = <Tag extends { id: number; printed?: boolean | null }>(
  palletTags: Tag[],
  setPalletTags: (tags: Tag[]) => void,
  selection: readonly (string | number)[]
) => {
  const [mutate, { loading, error }] = useMutation<PrintPalletTags, PrintPalletTagsVariables>(
    gql`
      mutation PrintPalletTags($ids: [Int]!) {
        printPalletTags(ids: $ids) {
          count
        }
      }
    `,
    {
      onCompleted: () => {
        enqueueSnackbar('Pallet Tags Printed', { variant: 'success', autoHideDuration: 1000 });
        setPalletTags(
          palletTags.map((tag) => {
            if (selection.includes(tag.id)) {
              return { ...tag, printed: true };
            }

            return tag;
          })
        );
      },
    }
  );
  const print = useCallback((variables: PrintPalletTagsVariables) => mutate({ variables }), [mutate]);

  return { print, loading, error };
};

export const useLookupPalletTag = (placeId?: number) => {
  const [lookupTag] = useLazyQuery<LookupPalletTag>(ScanningApi.LookupPalletTag);

  return async (tagNumber: string) => {
    const res = await lookupTag({ variables: { tagNumber, warehouseId: placeId } });
    return res.data?.palletTagLookup;
  };
};

const GetPalletTags = gql`
  query GetPalletTags($id: Int!) {
    lot(id: $id) {
      id
      weightAvailable
      metrics {
        unitsAvailable
      }
      product {
        id
        description
      }
      palletTags {
        id
        tagNumber
        cartonQuantity
        weight
        warehouseLocation {
          description
        }
      }
    }
  }
`;

export default { GetPalletTags };
